import http from 'utils/http';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import {
  getA2pBrandDetailUrl,
  getA2pBrandsUrl,
  getA2pCampaignDetailUrl,
  getA2pCampaignLinkableNumbersUrl,
  getA2pCampaignLinkNumbersUrl,
  getA2pCampaignPricingUrl,
  getA2pCampaignsUrl,
  getBusinessPhoneNumbersUrl,
  getHostSmsCheckImportabilityUrl,
  getHostSmsCreateImportOrderUrl,
  getHostSmsDetailUrl,
  getHostSmsResendOtpUrl,
  getHostSmsUrl,
  getHostSmsVerifyUrl
} from './apiUrls';
import {
  A2pBrand,
  A2pBrandPayload,
  A2pCampaign,
  A2pCampaignLinkPayload,
  A2pCampaignPayload,
  A2pCampaignPricing,
  A2pLinkableNumber,
  HostSms,
  HostSmsCreatePayload,
  HostSmsOrderPayload,
  PhoneNumberPurchase
} from './types';

export function getBusinessPhoneNumbers(
  params?: Record<string, unknown>
): Promise<{ data: PhoneNumberPurchase[]; meta: MetaApiResponse }> {
  return http.get(getBusinessPhoneNumbersUrl(), params);
}

export function getHostSmsList(params?: Record<string, unknown>): Promise<{ data: HostSms[]; meta: MetaApiResponse }> {
  return http.get(getHostSmsUrl(), params);
}

export function getHostSms(id: number | string): Promise<HostSms> {
  return http.get(getHostSmsDetailUrl(id));
}

export function createHostSms(payload: HostSmsCreatePayload): Promise<HostSms> {
  return http.post(getHostSmsUrl(), payload);
}

export function resendHostSmsOtp(id: number | string): Promise<HostSms> {
  return http.post(getHostSmsResendOtpUrl(id), {});
}

export function verifyHostSms(id: number | string, payload: { otp: string }): Promise<HostSms> {
  return http.post(getHostSmsVerifyUrl(id), payload);
}

export function checkHostSmsImportability(payload: {
  host_sms: { number: string };
}): Promise<{ importable: boolean; number: string; errors: { code: string; description: string }[] }> {
  return http.post(getHostSmsCheckImportabilityUrl(), payload);
}

export function createHostSmsImportOrder(
  id: number | string,
  payload: HostSmsOrderPayload,
  loa?: File | null
): Promise<HostSms> {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(`host_sms[${key}]`, String(value));
  });
  if (loa) formData.append('loa', loa);
  return http.post(getHostSmsCreateImportOrderUrl(id), formData);
}

export function uploadHostSmsLoa(id: number | string, file: File): Promise<HostSms> {
  const formData = new FormData();
  formData.append('loa', file);
  return http.patch(getHostSmsDetailUrl(id), formData);
}

export function deleteHostSms(id: number | string): Promise<unknown> {
  return http.delete(getHostSmsDetailUrl(id), undefined, { skipConfirm: true });
}

export function getA2pBrands(params?: Record<string, unknown>): Promise<{ data: A2pBrand[]; meta: MetaApiResponse }> {
  return http.get(getA2pBrandsUrl(), params);
}

export function createA2pBrand(payload: { a2p_brand: A2pBrandPayload }): Promise<A2pBrand> {
  return http.post(getA2pBrandsUrl(), payload);
}

export function updateA2pBrand(id: number | string, payload: { a2p_brand: A2pBrandPayload }): Promise<A2pBrand> {
  return http.patch(getA2pBrandDetailUrl(id), payload);
}

export function getA2pCampaigns(
  params?: Record<string, unknown>
): Promise<{ data: A2pCampaign[]; meta: MetaApiResponse }> {
  return http.get(getA2pCampaignsUrl(), params);
}

export function createA2pCampaign(payload: { a2p_campaign: A2pCampaignPayload }): Promise<A2pCampaign> {
  return http.post(getA2pCampaignsUrl(), payload);
}

export function updateA2pCampaign(
  id: number | string,
  payload: { a2p_campaign: A2pCampaignPayload }
): Promise<A2pCampaign> {
  return http.patch(getA2pCampaignDetailUrl(id), payload);
}

export function deleteA2pCampaign(id: number | string): Promise<unknown> {
  return http.delete(getA2pCampaignDetailUrl(id), undefined, { skipConfirm: true });
}

export function linkA2pCampaignNumbers(id: number | string, payload: A2pCampaignLinkPayload): Promise<A2pCampaign> {
  return http.post(getA2pCampaignLinkNumbersUrl(id), payload);
}

export function getA2pCampaignLinkableNumbers(id: number | string): Promise<{ data: A2pLinkableNumber[] }> {
  return http.get(getA2pCampaignLinkableNumbersUrl(id));
}

export function getA2pCampaignPricing(): Promise<{ data: A2pCampaignPricing }> {
  return http.get(getA2pCampaignPricingUrl());
}
